import { useState, useCallback } from 'react';
import { SnackType } from '@models/common.model';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useAppDispatch } from '@redux/hooks';
import { useTranslation } from '@hooks/useTranslation';
import { graphQlClient, graphQlMultipleUpload } from '@config/graphqlClient';
import NotesGraphQL from '@graphql/note.queries';
import { NoteCreateInput } from '@models/note.model';

interface UseCreateNoteReturn {
  createNote: (noteData: NoteCreateInput, files?: File[]) => Promise<boolean>;
  loading: boolean;
  error: string | null;
}

export const useCreateNote = (): UseCreateNoteReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');

  const createNote = useCallback(
    async (noteData: NoteCreateInput, files?: File[]): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const formData = {
          ...noteData,
        };

        let response;

        if (files && files.length > 0) {
          // Always use multiple upload to maintain consistency
          response = await graphQlMultipleUpload(
            files,
            'note',
            NotesGraphQL.mutations.createNote,
            formData,
          );
        } else {
          response = await graphQlClient.request(
            NotesGraphQL.mutations.createNote,
            {
              note: formData,
            },
          );
        }

        if (response?.createNote) {
          dispatch(
            appendActionMessage({
              message: 'Note created successfully',
              type: SnackType.SUCCESS,
            }),
          );
          return true;
        } else {
          throw new Error('Failed to create note');
        }
      } catch (e: any) {
        const errorMessage =
          e?.response?.errors[0]?.message ||
          e?.message ||
          localeCommon['requestError'];
        setError(errorMessage);

        dispatch(
          appendActionMessage({
            message: errorMessage,
            type: SnackType.ERROR,
          }),
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [dispatch, localeCommon, setError],
  );

  return {
    createNote,
    loading,
    error,
  };
};
